import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RoomsComposition } from '@shared/compositions';
import { DefaultImageDirective } from '@shared/directives';
import { NotificationType, SocketEvents } from '@core/enums';
import { untilDestroyed } from '@ngneat/until-destroy';
import { AlertMessageComponent, DynamicTabsComponent, IconComponent, TabDirective } from '@shared/components/ui';
import { DateTimeUtility, getAlertTypeByApprovedStatusOfEntity, isMobileView } from '@core/utils';
import { DialogRef } from '@ngneat/dialog';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { RoomFormComponent } from '@shared/components/forms';
import { RoomEntity } from '@core/entities';

@Component({
  selector: 'app-course-room',
  imports: [CommonModule, DynamicTabsComponent, IconComponent, TranslateModule, TabDirective, DefaultImageDirective, NgxSkeletonLoaderModule, AlertMessageComponent],
  templateUrl: './course-room.component.html',
  styleUrls: ['./course-room.component.scss', '../course-instructor/course-instructor.component.scss'],
})
export class CourseRoomComponent extends RoomsComposition implements OnInit {
  @Input() roomId: number;
  @Input() isOpenedInDialog = true;
  protected readonly isMobileView = isMobileView;

  constructor() {
    super();

    if (!isMobileView()) {
      this._dialogRef = inject(DialogRef);
    }
    this.isLoading = true;
  }

  @Input() set room(room: RoomEntity) {
    this.currentRoom = room;
  }

  get roomAvailability(): { name: string; time: string }[] {
    const availability = this.currentRoom?.availability;
    return availability.map((item) => {
      const weekdayNumber = isNaN(+item.weekday) ? DateTimeUtility.convertWeekdayNameToNumber(item.weekday) : +item.weekday;
      return {
        name: this.days.find((day) => day.number === +weekdayNumber)?.name,
        time: item.timeFrom + ' - ' + item.timeTo,
      };
    });
  }

  get alertType() {
    return getAlertTypeByApprovedStatusOfEntity(this.currentRoom);
  }

  get doesHavePhoneNumber() {
    return this.currentRoom?.phoneNo && this.currentRoom?.phoneNo !== '0';
  }

  get doesHaveEmail() {
    return this.currentRoom?.email;
  }

  ngOnInit() {
    if (!isMobileView() && this._dialogRef) {
      this.currentRoom = this.currentRoom ?? this._dialogRef.data.room;
      this.roomId = this.currentRoom?.id || this._dialogRef.data.roomId;
    } else {
      this.currentRoom = this.currentRoom ?? this._bottomSheet.currentSheetData.room;
      this.roomId = this.currentRoom?.id || this._bottomSheet.currentSheetData.roomId;
    }

    this._fetchRoom();

    this._socketService
      .fromEvent(SocketEvents.GetNotification)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data: NotificationType) => {
          if (data === NotificationType.Room) {
            this._fetchRoom();
          }
        },
      });
  }

  close() {
    if (!isMobileView() && this._dialogRef) {
      this._dialogRef.close();
    } else {
      this._bottomSheet.close();
    }
  }

  editRoom() {
    if (!isMobileView()) {
      this._dialogService.open(RoomFormComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-y-auto',
        data: {
          room: this.currentRoom,
        },
      });
    } else {
      this._bottomSheet.open(RoomFormComponent, {
        height: '95%',
        showCloseButton: false,
        data: {
          room: this.currentRoom,
        },
      });
    }
  }

  private _fetchRoom() {
    if (this.currentRoom && this.currentRoom.id) {
      this.roomId = this.currentRoom.id;
      this._fetchRoomDetails(this.roomId);
    } else if (this.roomId) {
      this._fetchRoomDetails(this.roomId);
    }
  }
}
